import { AGE_BANDS, ANAESTHETIC_PRIORITIES, ANAESTHETIC_PRIORITY_OPTIONS, ANAESTHETIC_SETTINGS, ANAESTHETIC_SETTING_OPTIONS, CAPABILITY_LEVELS, CARE_LEVELS, CASE_TYPES, careLevelLabel, COMMUNICATION_CATEGORIES, COMMUNICATION_CATEGORY_OPTIONS, EDT_ACTIVITY_OPTIONS, LEARNER_GRADES, LEARNER_GRADE_OPTIONS, REFERRAL_REASONS, TEACHING_DELIVERED, labelFor, } from './enums.js';
import { dayPart, isPaediatric, timeOfDay } from './derive.js';
import { HILLOS } from './hillos.js';
import { procedureByKey } from './procedures.js';
import { isCase, isCommunication, isProcedure } from './types.js';
/** Capability levels are 1–4 and the byLevel tuples have four slots. */
export function levelIndex(level) {
    return (level - 1);
}
export function inRange(entry, range) {
    const when = entry.occurredAt.getTime();
    return when >= range.from.getTime() && when <= range.to.getTime();
}
/**
 * The entries a report covers: inside the range, not deleted.
 *
 * Every function here starts with this. Soft-deleted rows still exist in the
 * database and in a sync payload, and a deleted entry appearing in an ARCP
 * summary would be a trainee over-stating their work without knowing it.
 */
export function selectEntries(entries, range) {
    return entries.filter((entry) => entry.deletedAt === undefined && inRange(entry, range));
}
/**
 * ICM entries only: inside the range, not deleted, and actually a Case, a
 * Procedure or a Communication.
 *
 * The last clause is defence in depth. `Entry` is `Case | Procedure |
 * Communication`, so an anaesthetic case cannot be passed here without a
 * cast — but `ReportRoute` and `DashboardRoute` both build their arrays with
 * `as Entry[]`, and a cast is exactly where a fourth record type would one
 * day slip in unnoticed. Every figure below reaches an ARCP summary, so a
 * leak would misstate a trainee's intensive care experience to the panel
 * assessing them. That is hazard H4, and one `filter` is a cheap price for
 * making it impossible rather than unlikely.
 */
function icmEntries(entries, range) {
    return selectEntries(entries, range).filter((entry) => isCase(entry) || isProcedure(entry) || isCommunication(entry));
}
function procedures(entries, range) {
    return selectEntries(entries, range).filter(isProcedure);
}
function cases(entries, range) {
    return selectEntries(entries, range).filter(isCase);
}
function communications(entries, range) {
    return selectEntries(entries, range).filter(isCommunication);
}
/**
 * One row per procedure actually performed, most performed first.
 *
 * Ordered by count rather than alphabetically: a panel reading the table wants
 * to see what this trainee does most, and an alphabetical list buries it.
 */
export function procedureCounts(entries, range) {
    const summaries = new Map();
    for (const procedure of procedures(entries, range)) {
        const definition = procedureByKey(procedure.procedureKey);
        let summary = summaries.get(procedure.procedureKey);
        if (summary === undefined) {
            summary = {
                procedureKey: procedure.procedureKey,
                label: definition?.label ?? procedure.procedureKey,
                total: 0,
                byLevel: [0, 0, 0, 0],
                variants: [],
                ultrasoundGuided: definition?.usGuidanceApplicable === true ? 0 : null,
                successes: null,
                complications: 0,
            };
            summaries.set(procedure.procedureKey, summary);
        }
        summary.total += 1;
        // `noUncheckedIndexedAccess` cannot see that a capability level is 1–4 and
        // the tuple has four slots, so the index is narrowed explicitly.
        summary.byLevel[levelIndex(procedure.capabilityLevel)] += 1;
        if (procedure.variant !== undefined) {
            const existing = summary.variants.find((entry) => entry.variant === procedure.variant);
            if (existing === undefined)
                summary.variants.push({ variant: procedure.variant, count: 1 });
            else
                existing.count += 1;
        }
        if (summary.ultrasoundGuided !== null && procedure.ultrasoundGuided === true) {
            summary.ultrasoundGuided += 1;
        }
        // `successes` stays null until something is recorded either way, so the PDF
        // can tell "all succeeded" from "nobody wrote it down".
        if (procedure.successful !== undefined) {
            summary.successes = (summary.successes ?? 0) + (procedure.successful ? 1 : 0);
        }
        if (procedure.complications !== undefined && procedure.complications.trim() !== '') {
            summary.complications += 1;
        }
    }
    for (const summary of summaries.values()) {
        summary.variants.sort((a, b) => b.count - a.count || a.variant.localeCompare(b.variant, 'en-GB'));
    }
    return [...summaries.values()].sort((a, b) => b.total - a.total || a.label.localeCompare(b.label, 'en-GB'));
}
/**
 * The recorded complications themselves, newest first.
 *
 * The PDF lists these in full rather than only counting them. A trainee who
 * records complications is demonstrating honesty, and the spec is explicit that
 * "none recorded" must be stated rather than the block omitted.
 */
export function complicationList(entries, range) {
    return procedures(entries, range)
        .filter((procedure) => (procedure.complications ?? '').trim() !== '')
        .map((procedure) => ({
        procedureKey: procedure.procedureKey,
        label: procedureByKey(procedure.procedureKey)?.label ?? procedure.procedureKey,
        occurredAt: procedure.occurredAt,
        complication: (procedure.complications ?? '').trim(),
    }))
        .sort((a, b) => b.occurredAt.getTime() - a.occurredAt.getTime());
}
/* --------------------------------------------------------------- case mix */
export function caseTypeCounts(entries, range) {
    const tally = new Map(CASE_TYPES.map((key) => [key, 0]));
    for (const record of cases(entries, range)) {
        tally.set(record.caseType, (tally.get(record.caseType) ?? 0) + 1);
    }
    return CASE_TYPES.map((key) => ({ key, label: key, count: tally.get(key) ?? 0 }));
}
/** One row per category, zero included — the same "no target, just counts" table as case type. */
export function communicationCategoryCounts(entries, range) {
    const tally = new Map(COMMUNICATION_CATEGORIES.map((key) => [key, 0]));
    for (const record of communications(entries, range)) {
        tally.set(record.category, (tally.get(record.category) ?? 0) + 1);
    }
    return COMMUNICATION_CATEGORIES.map((key) => ({
        key,
        label: labelFor(COMMUNICATION_CATEGORY_OPTIONS, key),
        count: tally.get(key) ?? 0,
    }));
}
/* ------------------------------------------------------ severity of illness */
/**
 * How many cases sat at each ICS level of care, and how many recorded none.
 *
 * `notRecorded` is returned rather than folded into a zero, because "no level
 * 0 cases" and "level not recorded on 40 cases" are completely different
 * statements and the PDF must not imply the first when it means the second.
 * The field is optional and always will be — see the friction rule.
 */
export function careLevelCounts(entries, range) {
    const tally = new Map(CARE_LEVELS.map((key) => [key, 0]));
    let notRecorded = 0;
    for (const record of cases(entries, range)) {
        // `=== undefined`, never truthiness: level 0 is a real level.
        if (record.careLevel === undefined) {
            notRecorded += 1;
            continue;
        }
        tally.set(record.careLevel, (tally.get(record.careLevel) ?? 0) + 1);
    }
    return {
        levels: CARE_LEVELS.map((key) => ({
            key: String(key),
            label: careLevelLabel(key),
            count: tally.get(key) ?? 0,
        })),
        notRecorded,
    };
}
/**
 * Capability level against level of care — the cross-tab that is the whole
 * reason the severity field earns its place on the form.
 *
 * A raw case count says how much work a trainee did. This says how much of it
 * was *hard*: level 4 capability on level 3 patients is the evidence of
 * progression an ARCP panel is actually looking for, and a logbook full of
 * level 4 on level 1 patients is a different story told in the same number of
 * entries.
 *
 * Rows are levels of care, columns are the four capability levels. Cases with
 * no level of care recorded are **excluded entirely** rather than given a row:
 * a partial cross-tab whose totals do not match the case count invites the
 * reader to do arithmetic that does not work. The count of them is returned
 * separately so the PDF can say how many it left out.
 */
export function capabilityByCareLevel(entries, range) {
    const rows = new Map(CARE_LEVELS.map((key) => [key, [0, 0, 0, 0]]));
    let excluded = 0;
    for (const record of cases(entries, range)) {
        if (record.careLevel === undefined) {
            excluded += 1;
            continue;
        }
        const row = rows.get(record.careLevel);
        if (row === undefined)
            continue;
        row[levelIndex(record.capabilityLevel)] += 1;
    }
    return {
        rows: CARE_LEVELS.map((careLevel) => {
            const byLevel = rows.get(careLevel) ?? [0, 0, 0, 0];
            return {
                careLevel,
                label: careLevelLabel(careLevel),
                byLevel,
                total: byLevel.reduce((sum, n) => sum + n, 0),
            };
        }),
        excluded,
    };
}
/**
 * A case with three reasons counts once against each, so these sum above the
 * number of cases. The PDF footnotes that; do not "fix" it here.
 */
export function referralReasonCounts(entries, range) {
    const tally = new Map(REFERRAL_REASONS.map((key) => [key, 0]));
    for (const record of cases(entries, range)) {
        for (const reason of record.reasonsForReferral ?? []) {
            tally.set(reason, (tally.get(reason) ?? 0) + 1);
        }
    }
    return REFERRAL_REASONS.map((key) => ({ key, label: key, count: tally.get(key) ?? 0 })).sort((a, b) => b.count - a.count);
}
/** Across everything — procedures and cases together, as the PDF shows it. */
export function ageBandCounts(entries, range) {
    const tally = new Map(AGE_BANDS.map((key) => [key, 0]));
    for (const entry of icmEntries(entries, range)) {
        tally.set(entry.ageBand, (tally.get(entry.ageBand) ?? 0) + 1);
    }
    return AGE_BANDS.map((key) => ({ key, label: key, count: tally.get(key) ?? 0 }));
}
export function capabilityCounts(entries, range) {
    const tally = [0, 0, 0, 0];
    for (const entry of icmEntries(entries, range))
        tally[levelIndex(entry.capabilityLevel)] += 1;
    return CAPABILITY_LEVELS.map((level) => ({
        key: String(level),
        label: `Level ${String(level)}`,
        count: tally[levelIndex(level)],
    }));
}
export function paediatricCount(entries, range) {
    return icmEntries(entries, range).filter(isPaediatric).length;
}
/** The 2×2 that shows a panel a trainee's out-of-hours work. */
export function temporalDistribution(entries, range) {
    const found = icmEntries(entries, range);
    const counts = {
        weekday_day: 0,
        weekday_night: 0,
        weekend_day: 0,
        weekend_night: 0,
    };
    for (const entry of found)
        counts[dayPart(entry.occurredAt)] += 1;
    return {
        ...counts,
        weekdayTotal: counts.weekday_day + counts.weekday_night,
        weekendTotal: counts.weekend_day + counts.weekend_night,
        dayTotal: counts.weekday_day + counts.weekend_day,
        nightTotal: counts.weekday_night + counts.weekend_night,
        total: found.length,
    };
}
/** Anything outside 08:00–19:59 on a weekday, or at any time at a weekend. */
export function outOfHoursCount(entries, range) {
    let count = 0;
    for (const entry of icmEntries(entries, range)) {
        if (dayPart(entry.occurredAt) !== 'weekday_day')
            count += 1;
    }
    return count;
}
function monthKey(date) {
    return `${String(date.getFullYear())}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}
/**
 * Every month in the range, including the empty ones.
 *
 * The gaps are the point: a chart that silently omits a quiet month draws a
 * smooth line through a period when someone was on nights, on leave, or not in
 * ICM at all, and that is a misrepresentation.
 */
export function entriesPerMonth(entries, range) {
    const found = icmEntries(entries, range);
    const buckets = new Map();
    const cursor = new Date(range.from.getFullYear(), range.from.getMonth(), 1);
    const last = new Date(range.to.getFullYear(), range.to.getMonth(), 1);
    while (cursor.getTime() <= last.getTime()) {
        buckets.set(monthKey(cursor), {
            key: monthKey(cursor),
            year: cursor.getFullYear(),
            month: cursor.getMonth() + 1,
            count: 0,
        });
        cursor.setMonth(cursor.getMonth() + 1);
    }
    for (const entry of found) {
        const bucket = buckets.get(monthKey(entry.occurredAt));
        if (bucket !== undefined)
            bucket.count += 1;
    }
    return [...buckets.values()];
}
/**
 * The progression chart: capability levels over time.
 *
 * Quarterly by default — monthly buckets over a training year are too sparse to
 * show the shift from level 1 to level 3, which is the story this chart exists
 * to tell.
 */
export function capabilityOverTime(entries, range, bucketBy = 'quarter') {
    const found = icmEntries(entries, range);
    const buckets = new Map();
    const keyFor = (date) => bucketBy === 'month'
        ? monthKey(date)
        : `${String(date.getFullYear())}-Q${String(Math.floor(date.getMonth() / 3) + 1)}`;
    const cursor = new Date(range.from.getFullYear(), range.from.getMonth(), 1);
    const last = new Date(range.to.getFullYear(), range.to.getMonth(), 1);
    while (cursor.getTime() <= last.getTime()) {
        const key = keyFor(cursor);
        if (!buckets.has(key))
            buckets.set(key, { key, byLevel: [0, 0, 0, 0], total: 0 });
        cursor.setMonth(cursor.getMonth() + 1);
    }
    for (const entry of found) {
        const bucket = buckets.get(keyFor(entry.occurredAt));
        if (bucket === undefined)
            continue;
        bucket.byLevel[levelIndex(entry.capabilityLevel)] += 1;
        bucket.total += 1;
    }
    return [...buckets.values()];
}
/**
 * A running total, monthly — or quarterly once the range is long enough that
 * monthly points stop being legible.
 *
 * Seven years is 84 monthly points, which on a phone is more points than the
 * chart has meaningful pixels, and it is the difference between a chart that
 * redraws instantly and one that stutters. The line says the same thing either
 * way; nothing is hidden, because the total is cumulative.
 */
export function cumulativeCount(entries, range) {
    const months = entriesPerMonth(entries, range);
    const buckets = months.length <= 36
        ? months
        : months.reduce((quarters, month, index) => {
            const last = quarters[quarters.length - 1];
            if (index % 3 === 0 || last === undefined)
                quarters.push({ ...month });
            else
                last.count += month.count;
            return quarters;
        }, []);
    let running = 0;
    return buckets.map((bucket) => {
        running += bucket.count;
        return { key: bucket.key, count: bucket.count, cumulative: running };
    });
}
function dayKey(date) {
    return `${String(date.getFullYear())}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}
/** The Monday on or before `date`, so a week of columns never starts ragged. */
function startOfWeek(date) {
    const start = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const day = start.getDay();
    start.setDate(start.getDate() - (day === 0 ? 6 : day - 1));
    return start;
}
/**
 * Every day from the Monday on or before `range.from` to `range.to`, including
 * the empty ones — the same "gaps are the point" principle `entriesPerMonth`
 * follows, one level finer. A consistency picture that silently smoothed over
 * the days nothing happened would not be a consistency picture.
 *
 * Takes plain dates rather than entries, so the same grid-and-gap-filling
 * logic serves two different questions from two different timestamps: a
 * trainee's own `occurredAt` (`dailyActivity`, below) and the operator
 * metrics page's `createdAt` across every account (`adminMetrics.ts`,
 * server-side) — "when did the clinical event happen" and "when did someone
 * use the app" are genuinely different questions that happen to bucket the
 * same way.
 */
export function dayGrid(dates, range) {
    const buckets = new Map();
    const cursor = startOfWeek(range.from);
    const last = new Date(range.to.getFullYear(), range.to.getMonth(), range.to.getDate());
    while (cursor.getTime() <= last.getTime()) {
        const key = dayKey(cursor);
        buckets.set(key, { date: key, count: 0 });
        cursor.setDate(cursor.getDate() + 1);
    }
    for (const date of dates) {
        const bucket = buckets.get(dayKey(date));
        if (bucket !== undefined)
            bucket.count += 1;
    }
    return [...buckets.values()];
}
/**
 * A trainee's own consistency picture: every day in range, live entries only.
 * Takes every kind of entry, the same as `hilloCoverage` — this is asking
 * "was anything logged today", not "was it clinical work", and a trainee who
 * spent the day teaching or in theatre should not see a false gap.
 */
export function dailyActivity(entries, range) {
    return dayGrid(selectEntries(entries, range).map((entry) => entry.occurredAt), range);
}
/**
 * Entries tagged against each HiLLO.
 *
 * Never rendered as a pass, a RAG rating or a progress bar, whatever the
 * `targetLevel` sitting next to it might invite. The app reports what happened;
 * the Educational Supervisor judges. See CURRICULUM.md.
 *
 * Takes every kind of entry, EDT included — HiLLOs 1 to 4 are the ones clinical
 * entries evidence worst, and EDT is where a trainee tags them. A coverage
 * table that omitted them would understate exactly the outcomes it is read for.
 */
export function hilloCoverage(entries, range) {
    const tally = new Map(HILLOS.map((hillo) => [hillo.number, 0]));
    for (const entry of selectEntries(entries, range)) {
        for (const hillo of entry.hillos ?? [])
            tally.set(hillo, (tally.get(hillo) ?? 0) + 1);
    }
    return HILLOS.map((hillo) => ({
        hillo: hillo.number,
        shortLabel: hillo.shortLabel,
        targetLevel: hillo.targetLevel,
        count: tally.get(hillo.number) ?? 0,
    }));
}
export function taggedEntryCount(entries, range) {
    return selectEntries(entries, range).filter((entry) => (entry.hillos ?? []).length > 0).length;
}
/**
 * One row per activity actually recorded, most recorded first — the same
 * ordering rule as the procedures table, for the same reason.
 *
 * `minutes` and `withDuration` are kept apart deliberately. Duration is
 * optional, so a total of 14 hours over 30 entries might mean 30 short
 * sessions or 4 recorded ones and 26 blanks. The PDF says which.
 */
export function edtActivityCounts(entries, range) {
    const summaries = new Map();
    for (const entry of selectEntries(entries, range)) {
        let summary = summaries.get(entry.activity);
        if (summary === undefined) {
            summary = {
                activity: entry.activity,
                label: labelFor(EDT_ACTIVITY_OPTIONS, entry.activity),
                count: 0,
                minutes: 0,
                withDuration: 0,
            };
            summaries.set(entry.activity, summary);
        }
        summary.count += 1;
        if (entry.durationMinutes !== undefined) {
            summary.minutes += entry.durationMinutes;
            summary.withDuration += 1;
        }
    }
    return [...summaries.values()].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, 'en-GB'));
}
export function edtStats(entries, range) {
    const found = selectEntries(entries, range);
    let minutes = 0;
    let withDuration = 0;
    let teachingDelivered = 0;
    let teachingDeliveredMinutes = 0;
    for (const entry of found) {
        const duration = entry.durationMinutes;
        if (duration !== undefined) {
            minutes += duration;
            withDuration += 1;
        }
        if (entry.activity === TEACHING_DELIVERED) {
            teachingDelivered += 1;
            if (duration !== undefined)
                teachingDeliveredMinutes += duration;
        }
    }
    return { total: found.length, minutes, withDuration, teachingDelivered, teachingDeliveredMinutes };
}
export function teachingOnEntries(entries, range) {
    const tally = new Map(LEARNER_GRADES.map((key) => [key, 0]));
    let caseCount = 0;
    let procedureCount = 0;
    let communicationCount = 0;
    for (const record of selectEntries(entries, range)) {
        const grades = record.teachingGrades ?? [];
        if (grades.length === 0)
            continue;
        // Explicit branches, not an `else` fallthrough — a fourth entry type
        // reaching here without its own branch must be a compile error, not a
        // silent miscount folded into whichever kind used to be "the other one".
        if (isCase(record))
            caseCount += 1;
        else if (isProcedure(record))
            procedureCount += 1;
        else if (isCommunication(record))
            communicationCount += 1;
        // An entry where a student and a nurse were both taught counts once
        // against each grade, and once overall. The PDF says so.
        for (const grade of grades)
            tally.set(grade, (tally.get(grade) ?? 0) + 1);
    }
    return {
        cases: caseCount,
        procedures: procedureCount,
        communications: communicationCount,
        total: caseCount + procedureCount + communicationCount,
        byGrade: LEARNER_GRADES.map((key) => ({
            key,
            label: labelFor(LEARNER_GRADE_OPTIONS, key),
            count: tally.get(key) ?? 0,
        })).filter((row) => row.count > 0),
    };
}
export function anaestheticStats(cases, range) {
    const found = selectEntries(cases, range);
    const byLevel = [0, 0, 0, 0];
    let theatre = 0;
    let acutePain = 0;
    let withAsa = 0;
    for (const record of found) {
        if (record.setting === 'theatre')
            theatre += 1;
        else
            acutePain += 1;
        if (record.asa !== undefined)
            withAsa += 1;
        byLevel[levelIndex(record.capabilityLevel)] += 1;
    }
    return { total: found.length, theatre, acutePain, withAsa, byLevel };
}
/** One row per setting actually recorded. Never a row reading zero. */
export function anaestheticSettingCounts(cases, range) {
    const found = selectEntries(cases, range);
    return ANAESTHETIC_SETTINGS.map((key) => ({
        key,
        label: labelFor(ANAESTHETIC_SETTING_OPTIONS, key),
        count: found.filter((record) => record.setting === key).length,
    })).filter((row) => row.count > 0);
}
/**
 * By NCEPOD priority, in the order the classification is taught rather than by
 * frequency — a reader scans it for the urgent end and expects to find it in
 * the same place every time.
 */
export function anaestheticPriorityCounts(cases, range) {
    const found = selectEntries(cases, range);
    return ANAESTHETIC_PRIORITIES.map((key) => ({
        key,
        label: labelFor(ANAESTHETIC_PRIORITY_OPTIONS, key),
        count: found.filter((record) => record.priority === key).length,
    })).filter((row) => row.count > 0);
}
/**
 * Procedures done before but not in the last six months.
 *
 * Informational, never a target and never a nag: the app does not tell a
 * trainee whether they have done enough, because FICM has set no such standard
 * and the app would be wrong the moment one changed. It says "you have not
 * recorded this for a while", which is a fact.
 *
 * `asOf` is passed in rather than read from the clock, like every date here.
 */
export function notDoneRecently(entries, range, asOf, monthsQuiet = 6) {
    const cutoff = new Date(asOf.getFullYear(), asOf.getMonth() - monthsQuiet, asOf.getDate());
    const lastDone = new Map();
    for (const procedure of procedures(entries, range)) {
        const existing = lastDone.get(procedure.procedureKey);
        if (existing === undefined) {
            lastDone.set(procedure.procedureKey, { last: procedure.occurredAt, total: 1 });
            continue;
        }
        existing.total += 1;
        if (procedure.occurredAt.getTime() > existing.last.getTime()) {
            existing.last = procedure.occurredAt;
        }
    }
    return [...lastDone.entries()]
        .filter(([, value]) => value.last.getTime() < cutoff.getTime())
        .map(([procedureKey, value]) => ({
        procedureKey,
        label: procedureByKey(procedureKey)?.label ?? procedureKey,
        lastDone: value.last,
        totalEver: value.total,
    }))
        .sort((a, b) => b.lastDone.getTime() - a.lastDone.getTime());
}
export function headlineStats(entries, range) {
    const found = icmEntries(entries, range);
    const days = Math.max(1, Math.round((range.to.getTime() - range.from.getTime()) / (24 * 60 * 60 * 1000)));
    const months = Math.max(1, days / 30.44);
    /*
     * One pass rather than six. At six thousand entries — a seven-year logbook —
     * a filter per statistic was enough to block the main thread for half a
     * second every time the date range changed, which the dashboard does on every
     * tap of the period selector.
     */
    let procedureCount = 0;
    let caseCount = 0;
    let communicationCount = 0;
    let outOfHours = 0;
    let paediatric = 0;
    const procedureKeys = new Set();
    for (const entry of found) {
        if (isProcedure(entry)) {
            procedureCount += 1;
            procedureKeys.add(entry.procedureKey);
        }
        else if (isCase(entry)) {
            caseCount += 1;
        }
        else if (isCommunication(entry)) {
            communicationCount += 1;
        }
        if (dayPart(entry.occurredAt) !== 'weekday_day')
            outOfHours += 1;
        if (isPaediatric(entry))
            paediatric += 1;
    }
    return {
        total: found.length,
        procedures: procedureCount,
        cases: caseCount,
        communications: communicationCount,
        distinctProcedureTypes: procedureKeys.size,
        averagePerMonth: Math.round((found.length / months) * 10) / 10,
        outOfHours,
        outOfHoursPercent: found.length === 0 ? 0 : Math.round((outOfHours / found.length) * 100),
        paediatric,
        days,
    };
}
/** Exported for the dashboard's day/night split, which has no weekend axis. */
export function timeOfDayCounts(entries, range) {
    let day = 0;
    let night = 0;
    for (const entry of icmEntries(entries, range)) {
        if (timeOfDay(entry.occurredAt) === 'day')
            day += 1;
        else
            night += 1;
    }
    return { day, night };
}
//# sourceMappingURL=aggregate.js.map